import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, TextField, Typography } from "@mui/material";
import { useNewsletterEditor } from "../context/NewsletterEditorContext";
import { getComponentPreset } from "../model/defaultComponents";
import { borderToSx } from "../model/borderUtils";
import { componentFillSx } from "../model/cellCanvasUtils";
import {
  buttonSizeStyle,
  contentLayoutWrapperSx,
  imageAlignMarginSx,
  imageWidthStyle,
} from "../utils/componentLayoutUtils";

const TEXT_COMPONENT_KEYS = ["heading-h1", "heading-h2", "heading-h3", "paragraph"];
const BUTTON_TEXT_KEY = "button";
const PLAIN_HTML_KEY = "plain-html";

const alignToFlex = (align) => {
  if (align === "center") {
    return "center";
  }
  if (align === "right") {
    return "flex-end";
  }
  return "flex-start";
};

const ComponentVisualPreview = ({ component, onSelect, forceEdit = false, onEditEnd }) => {
  const { updateProps, inlineEditComponentId, clearInlineTextEdit } = useNewsletterEditor();
  const { componentKey, props, id } = component;
  const preset = getComponentPreset(componentKey);
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);

  const isTextComponent = TEXT_COMPONENT_KEYS.includes(componentKey);
  const isButtonLabelEdit = componentKey === BUTTON_TEXT_KEY;
  const isPlainHtml = componentKey === PLAIN_HTML_KEY;
  const isEditing = editing || forceEdit || inlineEditComponentId === id;

  useEffect(() => {
    if ((forceEdit || inlineEditComponentId === id) && (isTextComponent || isButtonLabelEdit)) {
      setEditing(true);
    }
  }, [forceEdit, inlineEditComponentId, id, isTextComponent, isButtonLabelEdit]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const wrapperSx = {
    ...componentFillSx,
    cursor: "pointer",
  };

  const finishEdit = () => {
    setEditing(false);
    clearInlineTextEdit();
    onEditEnd?.();
  };

  const handleClick = (e) => {
    e.stopPropagation();
    onSelect({ type: "component", id });
  };

  const commitText = (value) => {
    updateProps(id, { text: value }, true, { recordHistory: true });
    finishEdit();
  };

  const startEditing = (e) => {
    e.stopPropagation();
    if (isTextComponent || isButtonLabelEdit) {
      setEditing(true);
    }
  };

  if (isPlainHtml) {
    return (
      <Box onClick={handleClick} sx={wrapperSx}>
        {props?.html ? (
          <Box
            sx={{ width: "100%", lineHeight: 1.5, wordBreak: "break-word" }}
            dangerouslySetInnerHTML={{ __html: props.html }}
          />
        ) : (
          <Typography variant="caption" color="text.secondary">
            Usa Modifica HTML nelle proprietà
          </Typography>
        )}
      </Box>
    );
  }

  if (componentKey === "image") {
    const layoutSx = contentLayoutWrapperSx(props);
    const imgSx = {
      display: "block",
      boxSizing: "border-box",
      maxWidth: "100%",
      ...imageWidthStyle(props),
      ...imageAlignMarginSx(props),
      ...borderToSx(props?.border),
    };
    return (
      <Box onClick={handleClick} sx={wrapperSx}>
        <Box sx={layoutSx}>
          {props?.src ? (
            <Box component="img" src={props.src} alt={props.alt || ""} sx={imgSx} />
          ) : (
            <Box sx={{ width: "100%", minWidth: 120, py: 2, textAlign: "center", backgroundColor: "#f1f5f9", color: "#94a3b8", fontSize: 12 }}>
              Immagine non impostata
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  if (componentKey === "button") {
    const layoutSx = contentLayoutWrapperSx(props);
    const frameSx = borderToSx(props?.border);
    if (isEditing) {
      return (
        <Box sx={{ ...wrapperSx, ...layoutSx }} onClick={(e) => e.stopPropagation()}>
          <TextField
            inputRef={inputRef}
            fullWidth
            size="small"
            value={props?.text ?? ""}
            onChange={(e) => updateProps(id, { text: e.target.value }, true, { recordHistory: false })}
            onBlur={(e) => commitText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                finishEdit();
              }
              if (e.key === "Enter") {
                e.preventDefault();
                commitText(e.target.value);
              }
            }}
            sx={{ maxWidth: 320 }}
          />
        </Box>
      );
    }
    return (
      <Box onClick={handleClick} onDoubleClick={startEditing} sx={wrapperSx}>
        <Box sx={layoutSx}>
          <Box
            component="span"
            sx={{
              backgroundColor: props?.backgroundColor || "#0b3d87",
              color: props?.color || "#fff",
              fontSize: 14,
              fontWeight: 600,
              textAlign: props?.align || "center",
              ...buttonSizeStyle(props),
              ...frameSx,
            }}
          >
            {props?.text || "Bottone"}
          </Box>
        </Box>
      </Box>
    );
  }

  if (isTextComponent) {
    const Tag = componentKey === "paragraph" ? "p" : componentKey === "heading-h1" ? "h1" : componentKey === "heading-h2" ? "h2" : "h3";
    const textWrapperSx = {
      ...wrapperSx,
      alignItems: alignToFlex(props?.align),
      justifyContent: "flex-start",
    };
    if (isEditing) {
      return (
        <Box sx={{ ...textWrapperSx, mb: 0 }} onClick={(e) => e.stopPropagation()}>
          <TextField
            inputRef={inputRef}
            fullWidth
            multiline
            size="small"
            value={props?.text ?? ""}
            onChange={(e) => updateProps(id, { text: e.target.value }, true, { recordHistory: false })}
            onBlur={(e) => commitText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                finishEdit();
              }
              if (e.key === "Enter" && !e.shiftKey && componentKey !== "paragraph") {
                e.preventDefault();
                commitText(e.target.value);
              }
            }}
            sx={{ flex: 1, "& .MuiInputBase-root": { fontSize: props?.fontSize || "14px" } }}
          />
        </Box>
      );
    }
    return (
      <Box onClick={handleClick} onDoubleClick={startEditing} sx={textWrapperSx}>
        <Box
          component={Tag}
          sx={{
            m: 0,
            width: "100%",
            boxSizing: "border-box",
            textAlign: props?.align || "left",
            color: props?.color || "#333",
            fontSize: props?.fontSize || "14px",
            fontWeight: props?.fontWeight || "400",
            lineHeight: 1.5,
            wordBreak: "break-word",
            whiteSpace: "pre-line",
            ...(props?.backgroundColor ? { backgroundColor: props.backgroundColor } : {}),
            ...borderToSx(props?.border),
          }}
        >
          {props?.text || preset.label}
        </Box>
      </Box>
    );
  }

  return (
    <Box onClick={handleClick} sx={wrapperSx}>
      <Typography variant="caption">{props?.text || preset.label}</Typography>
    </Box>
  );
};

ComponentVisualPreview.propTypes = {
  component: PropTypes.object.isRequired,
  onSelect: PropTypes.func.isRequired,
  forceEdit: PropTypes.bool,
  onEditEnd: PropTypes.func,
};

export default ComponentVisualPreview;
export { TEXT_COMPONENT_KEYS };
 